// 격자 + 열 매핑 → 공종별 항목. 칸 읽기는 붙여넣기와 한 벌(`rowFromCells`)을 쓴다.

import { calcItem } from "../calc";
import { convertUnitPrice } from "../margin";
import { VatMode } from "../../money/vat";
import { normLabel } from "./detect";
import {
  ColumnMap,
  ImportGroup,
  ImportParse,
  MAX_IMPORT_ROWS,
  RowKind,
} from "./types";
import { applyAmountColumns, rowFieldsOf } from "./apply-columns";
import { CellParseContext, parseNumber, rowFromCells } from "../editor/paste";
import { FilledItemInput } from "../editor/types";

// 통째로 이 말이면 집계 줄이다(「계」 같은 짧은 말은 포함으로 보면 오탐이 난다).
const TOTAL_EXACT =
  /^(계|합|이윤|기업이윤|일반관리비|관리비|절사|단수정리|끝전정리|총액|총공사비|직접공사비|순공사비|공사비계|간접공사비|공급가액|공급가|계약금액|견적금액|공사금액)$/;
// 이 말이 들어 있으면 집계 줄이다.
const TOTAL_WORDS = ["소계", "합계", "총계", "부가세", "부가가치세", "vat", "공과잡비", "공과잡비및이윤"];
// 원본 합계로 읽을 줄.
const GRAND_WORDS = ["합계", "총계"];
// 담지 않는 간접비 줄 — 금액을 따로 모아 알린다.
const INDIRECT_EXACT = /^(공과잡비|공과잡비및이윤|이윤|기업이윤|일반관리비|관리비)$/;
// 원본 합계 후보 — 이 순서로 계산 합계와 맞는지 본다(공사비 계 → 공급가액 → 합계).
const DIRECT_EXACT = /^(순공사비|공사비계|직접공사비)$/;
const SUPPLY_EXACT = /^(공급가액|공급가)$/;
type TotalKind = "indirect" | "direct" | "supply" | "grand";
// 부가세를 따로 적은 줄 — 공급가액 줄과 함께 「원본 단가는 부가세 별도」 의 단서.
const VAT_WORDS = ["부가세", "부가가치세", "vat"];
const CANDIDATE_ORDER: TotalKind[] = ["direct", "supply", "grand"];

const stripParen = (t: string): string => t.replace(/\(.*\)$/, "");

/** 집계 줄의 이름인가 — 소계·합계·부가세·공과잡비 등. */
export const isTotalLabel = (text: string): boolean => {
  const raw = text.replace(/\s/g, "").toLowerCase();
  if (raw === "") return false;
  const t = stripParen(raw);
  return TOTAL_EXACT.test(t) || TOTAL_WORDS.some((w) => raw.includes(w));
};

const isGrandLabel = (text: string): boolean => {
  const t = text.replace(/\s/g, "");
  return !t.includes("소계") && GRAND_WORDS.some((w) => t.includes(w));
};

const isVatSplitLabel = (text: string): boolean => {
  const raw = text.replace(/\s/g, "").toLowerCase();
  return SUPPLY_EXACT.test(stripParen(raw)) || VAT_WORDS.some((w) => raw.includes(w));
};

const totalKindOf = (text: string): TotalKind | null => {
  const t = stripParen(text.replace(/\s/g, "").toLowerCase());
  if (INDIRECT_EXACT.test(t) || t.includes("공과잡비")) return "indirect";
  if (DIRECT_EXACT.test(t)) return "direct";
  if (SUPPLY_EXACT.test(t)) return "supply";
  return isGrandLabel(text) ? "grand" : null;
};

// 공종 번호 — 1·(1)·가·Ⅰ. 「가-하」 범위는 「주」 같은 글자까지 걸려 열네 글자만 쓴다.
const HEAD_NO = "(?:[0-9]{1,2}|[ⅰ-ⅹⅠ-Ⅹ]{1,4}|[가나다라마바사아자차카타파하])";
const HEAD_NO_PREFIX = new RegExp(`^(?:\\(\\s*${HEAD_NO}\\s*\\)|${HEAD_NO}[.)\\]])\\s*`);
// 번호 뒤에 글자가 오는 공종 머리행(「1.5m」 같은 수는 빼려고 숫자·점으로 시작하면 안 된다).
const NUMBERED_HEAD = new RegExp(`${HEAD_NO_PREFIX.source}[^\\d\\s.]`);

/** 공종 머리행 글자에서 앞 번호(「1.」·「(1)」·「가)」·「Ⅱ.」)를 떼어 낸다. */
export const cleanTradeName = (text: string): string =>
  text
    .trim()
    .replace(/^[■□▶▷●○◆◇ㅁ\-]\s*/, "")
    .replace(HEAD_NO_PREFIX, "")
    .replace(/^\[(.*)\]$/, "$1")
    .trim();

const NUMERIC_FIELDS = new Set(["qty", "material_unit_price", "labor_unit_price", "cost_unit_price", "unit"]);
const TEXT_FIELDS = new Set(["trade", "space_code", "scope", "item"]);

const isNumberText = (text: string): boolean => {
  const t = text.trim();
  return t !== "" && parseNumber(t) !== null;
};

// 머리행 글자로 보기엔 이상한 것(주석·긴 문장).
const looksLikeNote = (text: string): boolean => /^[※*#(\-]/.test(text) || text.length > 30;

const amountOf = (text: string): number | null => {
  const n = parseNumber(text);
  return n === null || text.trim() === "" ? null : n;
};

const PART_FIELDS = new Set(["amount_part", "material_amount", "labor_amount"]);

// 원본 합계 줄의 금액 — 「금액 합계」 열이 있으면 그것, 없으면 자재·노무 금액 열의 합.
const grandAmountOf = (cells: string[], columns: ColumnMap): number | null => {
  const totals = columns
    .map((f, i) => (f === "amount_total" ? amountOf(cells[i] ?? "") : null))
    .filter((v): v is number => v !== null);
  if (totals.length > 0) return Math.max(...totals);
  const parts = columns
    .map((f, i) => (f !== null && PART_FIELDS.has(f) ? amountOf(cells[i] ?? "") : null))
    .filter((v): v is number => v !== null);
  return parts.length > 0 ? parts.reduce((a, b) => a + b, 0) : null;
};

/** 항목 줄들의 자재+노무 합계(견적 계산과 같은 `calcItem`). 셀 수 없는 수가 되면 null. */
export const totalOf = (items: FilledItemInput[]): number | null => {
  try {
    const sum = items.reduce((acc, item) => acc + calcItem(item).amount, 0);
    return Number.isSafeInteger(sum) ? sum : null;
  } catch {
    return null;
  }
};

/** 격자 → 공종별 항목. 공종은 공종 열(위에서 이어 받기) 또는 공종 머리행(글자 칸 하나뿐인 줄)이 정한다. */
export const parseImportRows = (
  grid: string[][],
  columns: ColumnMap,
  dataStart: number,
  ctx: CellParseContext
): ImportParse => {
  const fields = rowFieldsOf(columns);
  const tradeCol = columns.indexOf("trade");
  const itemCol = columns.indexOf("item");
  const labelCols = columns
    .map((f, i) => (f !== null && TEXT_FIELDS.has(f) ? i : -1))
    .filter((i) => i >= 0);

  const groups: ImportGroup[] = [];
  const byName = new Map<string, ImportGroup>();
  let current = "";
  let rowCount = 0;
  let unclearCells = 0;
  let unclearRows = 0;
  let skippedTotals = 0;
  let skippedBlank = 0;
  let indirectTotal = 0;
  let expenseMerged = false;
  let pricedFromAmount = 0;
  let vatSplitRows = false;
  const candidates = new Map<TotalKind, { amount: number; label: string }>();
  let tooMany = false;
  const kinds: (RowKind | null)[] = grid.map(() => null);

  const groupOf = (name: string): ImportGroup => {
    const found = byName.get(name);
    if (found) return found;
    const group: ImportGroup = { name, items: [] };
    byName.set(name, group);
    groups.push(group);
    return group;
  };

  for (let r = dataStart; r < grid.length; r += 1) {
    const cells = grid[r];
    const filled = cells
      .map((v, i) => ({ v: (v ?? "").trim(), i }))
      .filter((c) => c.v !== "");
    if (filled.length === 0) continue;

    // 집계 줄 — 글자 칸(공종·부위·작업내역) 또는 첫 칸이 「소계」 류.
    const labels = [...labelCols.map((i) => (cells[i] ?? "").trim()), filled[0].v];
    const totalLabel = labels.find((t) => t !== "" && !isNumberText(t) && isTotalLabel(t));
    if (totalLabel !== undefined) {
      skippedTotals += 1;
      kinds[r] = "total";
      if (labels.some((t) => t !== "" && !isNumberText(t) && isVatSplitLabel(t))) vatSplitRows = true;
      const named = labels.find((t) => t !== "" && !isNumberText(t) && totalKindOf(t) !== null);
      const kind = named === undefined ? null : totalKindOf(named);
      const amount = kind === null ? null : grandAmountOf(cells, columns);
      if (kind === "indirect" && amount !== null) indirectTotal += amount;
      else if (kind !== null && named !== undefined && amount !== null && !candidates.has(kind)) {
        candidates.set(kind, { amount, label: named });
      }
      continue;
    }

    // 공종 머리행 — 수량·단가·단위가 비고 글자 칸이 하나뿐인 줄.
    const numericEmpty = columns.every(
      (f, i) => f === null || !NUMERIC_FIELDS.has(f) || (cells[i] ?? "").trim() === ""
    );
    const texts = filled.filter((c) => !isNumberText(c.v));
    if (numericEmpty && texts.length === 1) {
      const at = texts[0].i;
      const inLabel = labelCols.includes(at) || at <= Math.max(itemCol, 0);
      // 번호 붙은 글자(「(1) 철거」)는 공종 머리행. 공종 열이 있는데 작업내역 칸에만 번호 없는 글자가 있으면 숫자 없는 항목(「별도」 등)으로 본다.
      const head = NUMBERED_HEAD.test(texts[0].v)
        ? inLabel
        : !looksLikeNote(texts[0].v) && inLabel && (tradeCol < 0 || at !== itemCol);
      if (head) {
        current = cleanTradeName(texts[0].v);
        kinds[r] = "trade";
        continue;
      }
    }

    if (tradeCol >= 0) {
      const t = (cells[tradeCol] ?? "").trim();
      if (t !== "") current = cleanTradeName(t);
    }

    const item = itemCol >= 0 ? (cells[itemCol] ?? "").trim() : "";
    if (item === "") {
      skippedBlank += 1;
      kinds[r] = "blank";
      continue;
    }

    if (rowCount >= MAX_IMPORT_ROWS) {
      tooMany = true;
      break;
    }
    const built = rowFromCells(cells, fields, ctx);
    const applied = applyAmountColumns(built.row, cells, columns);
    const row = applied.row;
    const unclear = built.unclear + applied.unclear;
    if (applied.priced) pricedFromAmount += 1;
    if (applied.expenseMerged) expenseMerged = true;
    rowCount += 1;
    kinds[r] = "item";
    unclearCells += unclear;
    if (unclear > 0) unclearRows += 1;
    groupOf(current).items.push(row);
  }

  const computedTotal = totalOf(groups.flatMap((g) => g.items));
  // 계산 합계와 맞는 후보가 있으면 그 줄, 없으면 공사비 계 → 공급가액 → 합계 순의 첫 줄.
  const ordered = CANDIDATE_ORDER.map((k) => candidates.get(k)).filter(
    (c): c is { amount: number; label: string } => c !== undefined
  );
  const original = ordered.find((c) => c.amount === computedTotal) ?? ordered[0] ?? null;
  return {
    kinds,
    groups,
    rowCount,
    unclearCells,
    unclearRows,
    skippedTotals,
    skippedBlank,
    originalTotal: original?.amount ?? null,
    originalTotalLabel: original?.label ?? null,
    indirectTotal,
    expenseMerged,
    pricedFromAmount,
    vatSplitRows,
    computedTotal,
    tooMany,
  };
};

// 원본 단가 기준이 견적 방식과 다르면 자재·노무 단가를 바꿔 담는다(같으면 그대로) · 원가는 늘 부가세 빼고라 그대로.
export const convertImportPrices = <T extends { items: FilledItemInput[] }>(groups: T[], from: VatMode, to: VatMode): T[] =>
  from === to
    ? groups
    : groups.map((g) => ({
        ...g,
        items: g.items.map((it) => ({
          ...it,
          material_unit_price: convertUnitPrice(it.material_unit_price, from, to),
          labor_unit_price: convertUnitPrice(it.labor_unit_price, from, to),
        })),
      }));

/** 공종 이름 비교용 — 번호·「공사」·공백을 뗀다. */
export const tradeKeyOf = (name: string): string =>
  normLabel(cleanTradeName(name)).replace(/공사$/, "");
